import PropTypes from 'prop-types';
import React from 'react';

import { Header, SideLine, Footer, Background } from '$partials/global/index.js';
import { useAjaxListing } from '$hooks/ajax-listing';
import { AjaxListingContext, InfinitePagination } from '$partials/base/ajax-listing.jsx';
import { EventItem } from '$partials/listings';
import { useSingleSelectFilter } from '$hooks/filters';
import { DetailDialogProvider } from '$pages/detail/detail-page-provider';
import { getBackgroundColours } from '$utilities/site-context';
import { getSidebarTitles } from '$utilities/sidebar-titles';

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @param {PropTypes.InferProps<typeof propTypes>} props
 */
const EventListingPage = (props) => {
    const page = props.data.page;

    const fundsFilter = useSingleSelectFilter(page?.items?.filters?.funds);

    const listing = useAjaxListing({
        infinite: true,
        items: page.items?.edges,
        path: `/api/v1/listings/${encodeURIComponent(props.data?.uri)}`,
        page: page?.items?.pageInfo?.page,
        totalItems: page.items?.total,
        totalPages: page.items?.totalPages,
        query: {
            'funds': fundsFilter.value,
        }
    });

    const now = new Date();

    const futureFilter = (item) => {
        const eventDate = new Date(item?.node?.eventDate);
        if (eventDate.getTime() > now.getTime()) {
            return true;
        }
    };
    const pastFilter = (item) => {
        const eventDate = new Date(item?.node?.eventDate);
        if (eventDate.getTime() < now.getTime()) {
            return true;
        }
    };

    return (
        <>
            <Background {...getBackgroundColours()} />

            {/* Navigation */}
            <Header navigation={props.data.navigation} />

            {/* Main content */}
            <main
                className='pt-40 sm:pt-50 md:pt-100 min-h-screen relative'
                id='main-content'>
                <SideLine/>

                <div className='layout-container'>
                    <div className='flex flex-wrap layout-negative-margin'>
                        {/* Heading */}
                        <div className='sm:w-9/12 layout-item-padding'>
                            <h1 className='type-style-2'>
                                {page?.headline}
                            </h1>
                        </div>
                    </div>
                </div>

                <div className='layout-container-padding-right max-w-container bg-white mt-40 pb-70 sm:py-70 md:py-100 rounded-md'>
                    <DetailDialogProvider>
                        <AjaxListingContext.Provider value={listing}>
                            <AjaxListingContext.Consumer>
                                {(listingState) => (
                                    <>
                                        {listingState?.items?.length > 0 ? (
                                            <section
                                                aria-busy={listingState.loading}
                                                aria-label='Events'
                                                className='pt-100 sm:pt-10'
                                                role='feed'>

                                                {/* Upcoming */}
                                                {listingState.items.filter(futureFilter)?.length > 0 && (
                                                    <div className='flex sm:pr-40 md:mb-70 sm:mb-50'>
                                                        <div className='w-sidebar pt-75 sm:pt-10'>
                                                            <div className='flex flex-col items-center'>
                                                                <h2 className='type-style-8 align-middle rotate-180 writing-mode-vertical-rl max-sm:inline-block'>
                                                                    {getSidebarTitles()?.eventListingUpcomingSidebarTitle}
                                                                </h2>
                                                            </div>
                                                        </div>
                                                        <div className='w-full grid grid-cols-1 sm:grid-cols-2 md:grid-cols-1 sm:gap-x-70 gap-y-60 sm:gap-y-40 md:gap-y-80 layout-item-padding md:ml-10'>
                                                            {listingState.items.filter(futureFilter).map(({node: event}) => {
                                                                return (
                                                                    <EventItem
                                                                        key={event.id}
                                                                        openToModal={true}
                                                                        type='Upcoming'
                                                                        {...event} />
                                                                );
                                                            })}
                                                        </div>
                                                    </div>
                                                )}

                                                {listingState.items.filter(pastFilter)?.length > 0 && (
                                                    <>
                                                        <div className='flex sm:pr-40'>
                                                            <div className='w-sidebar pt-75 sm:pt-10'>
                                                                <div className='flex flex-col items-center'>
                                                                    <h2 className='type-style-8 align-middle rotate-180 writing-mode-vertical-rl max-sm:inline-block'>
                                                                    </h2>
                                                                </div>
                                                            </div>
                                                            <h2 className={`layout-item-padding-left ml-15 sm:-ml-15 type-style-3 ${listingState.items.filter(futureFilter)?.length > 0 ? 'mt-55 sm:my-50 md:my-80' : 'sm:mb-50 md:mb-80'} mb-75 md:w-6/12`}>
                                                                What you&apos;ve missed at Phoenix Court
                                                            </h2>
                                                        </div>

                                                        <div className='flex sm:pr-40'>
                                                            <div className='w-sidebar pt-75 sm:pt-10'>
                                                                <div className='flex flex-col items-center'>
                                                                    <h2 className='type-style-8 align-middle rotate-180 writing-mode-vertical-rl max-sm:inline-block'>
                                                                        {getSidebarTitles()?.eventListingPastSidebarTitle}
                                                                    </h2>
                                                                </div>
                                                            </div>
                                                            <div className='w-full grid grid-cols-1 sm:grid-cols-2 md:grid-cols-1 sm:gap-x-70 gap-y-60 sm:gap-y-40 md:gap-y-80 layout-item-padding md:ml-10'>
                                                                {listingState.items.filter(pastFilter).map(({node: event}) => {
                                                                    return (
                                                                        <EventItem
                                                                            key={event.id}
                                                                            openToModal={true}
                                                                            type='Past'
                                                                            {...event} />
                                                                    );
                                                                })}
                                                            </div>
                                                        </div>
                                                    </>
                                                )}

                                                <InfinitePagination>
                                                </InfinitePagination>
                                            </section>
                                        ) : (
                                            <div className='flex sm:pr-40 bg-transparent'>
                                                <div className='w-sidebar pt-75 sm:pt-10'>
                                                    <div className='flex flex-col items-center'>
                                                        <h2 className='type-style-8 align-middle rotate-180 writing-mode-vertical-rl max-sm:inline-block'>
                                                        </h2>
                                                    </div>
                                                </div>
                                                <div className='py-30'>
                                                    No upcoming or past events.
                                                </div>
                                            </div>

                                        )}
                                    </>
                                )}
                            </AjaxListingContext.Consumer>
                        </AjaxListingContext.Provider>
                    </DetailDialogProvider>
                </div>
            </main>

            {/* Footer */}
            {props.data?.footer && (
                <Footer {...props.data.footer}/>
            )}
        </>
    );
};

/* ========================================================================= */
/* Typing and exports */
/* ========================================================================= */

const propTypes = {
    data: PropTypes.shape({
        page: PropTypes.shape({
            headline: PropTypes.string,
            items: PropTypes.object,
        }),
        navigation: PropTypes.object,
        footer: PropTypes.object,
        uri: PropTypes.string,
    }),
    domain: PropTypes.string,
};

EventListingPage.propTypes = propTypes;

export default EventListingPage;
